import arrayMove from 'array-move';
import * as React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  defaultDropAnimationSideEffects,
  DropAnimation,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { DragHandleProvider } from '../../hooks/useDragHandle';
import { INPUT, TEXTAREA, BUTTON, SELECT } from '../../utils/tagNames';

/**
 * Drop animation configuration.
 */
const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: "0.4"
      }
    }
  })
};

/**
 * Drag overlay to mirror the active (dragged) item.
 */
const SortableOverlay = ({ children }) => {
  return (
    <DragOverlay dropAnimation={dropAnimationConfig}>
      {children}
    </DragOverlay>
  );
};

/**
 * A helper component that wraps (clones) an existing element with sortable functionality.
 * This version works for both table rows (<tr>) and generic block elements (<div>, etc.).
 *
 * The component receives an item element (as a React element), an id, and optional props.
 */
const SortableItem = ({
  id,
  itemElement,
  helperClass = '',
  useDragHandle = false,
  order
}) => {
  // const { attributes, listeners, setNodeRef, transform, transition } =
  //   useSortable({ id });
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    data: { accessibleLabel: `Row ${order}` },  // provide extra data here
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const tabIndex =
    itemElement.props.tabIndex !== undefined ? itemElement.props.tabIndex : 0;

  // Merge in our custom accessible label.
  // If accessibleLabel is provided, use it; otherwise, fall back
  // to any aria-label already present or a default message.
  const mergedAttributes = {
    ...attributes,
    'aria-label': `Row ${order + 1}`,
  };

  const element = React.cloneElement(itemElement, {
    ref: setNodeRef,
    style: { ...(itemElement.props.style || {}), ...style },
    className: `${itemElement.props.className || ''} ${helperClass}`.trim(),
    tabIndex,
    ...attributes,
    ...listeners,
  });

  if (useDragHandle) {
    // Optionally, if you want to wrap with DragHandleProvider and the provider
    // leverages these properties for its own logic, you could return a wrapped version:
    return (
      <DragHandleProvider attributes={{ ...mergedAttributes, tabIndex: -1 }} listeners={listeners}>
        {element}
      </DragHandleProvider>
    );
  }

  return element;
};

/**
 * Custom keyboard sensor that respects certain element types.
 */
export class KeyboardSensorThatHandlesElementType extends KeyboardSensor {
  static activators = [
    {
      eventName: 'onKeyDown',
      handler: ({ nativeEvent: event }: React.KeyboardEvent<Element>) => {
        return shouldHandleEvent(event);
      }
    }
  ];
}

/**
 * Custom pointer sensor that respects certain element types.
 */
export class PointerSensorThatHandlesElementType extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent: event }: React.PointerEvent<Element>) => {
        return shouldHandleEvent(event);
      }
    }
  ];
}

/**
 * Helper function to determine whether the event should be handled.
 */
function shouldHandleEvent(event) {
  if (event.key === "Tab") return false; // Allow default tab behavior
  const activeElement = document.activeElement;
  const tagNames = [INPUT, SELECT, TEXTAREA, BUTTON];
  // Prevent sensor activation if focused on input, select, textarea, or button.
  if (
    activeElement &&
    (
      tagNames.includes(activeElement.tagName) ||
      activeElement.attributes['contenteditable']?.value === 'true' ||
      event.srcElement['isContentEditable']
    )
  ) {
    return false;
  }
  return true;
}

/**
 * The main SortableList component.
 *
 * This component accepts a `data` prop that is an array of React elements.
 * These can be either table row elements (<tr>)—when the list is rendered inside a <tbody>—
 * or generic elements (<div>, etc.) for other use cases.
 *
 * The component clones each element to inject sortable behavior.
 */
export const SortableList = ({ data, sortCallback, helperClass, useDragHandle }) => {
  const [activeId, setActiveId] = React.useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  /**
   * Handle drag end events to update the order.
   */
  const handleDragEnd = event => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = data.findIndex(item => String(item.key) === String(active.id));
      const newIndex = data.findIndex(item => String(item.key) === String(over.id));
      const newData = arrayMove(data, oldIndex, newIndex);
      sortCallback(newData);
      // Optionally, restore focus after reordering.
      setTimeout(() => {
        const movedElement = document.querySelector(`[data-id="${active.id}"]`);
        movedElement?.focus();
      }, 0);
    }
    setActiveId(null);
  };

  /**
   * Handle drag start events.
   */
  const handleDragStart = event => {
    setActiveId(event.active.id);
  };

  /**
   * Renders a sortable item by its id from the data array.
   */
  const renderItemById = (id, applyHelperClass = false) => {
    const index = data.findIndex(item => String(item.key) === String(id));
    const itemElement = data[index];
    // Compute order based on the index in the data array, defaulting to a 1-based index.
    const computedOrder = index >= 0 ? index + 1 : 'unknown';
    return itemElement ? (
      <SortableItem
        helperClass={applyHelperClass ? helperClass : ''}
        id={itemElement.key}
        itemElement={itemElement}
        key={itemElement.key}
        order={computedOrder}
        useDragHandle={useDragHandle}
      />
    ) : null;
  };

  return (
    <DndContext
      accessibility={{
        announcements: {
          onDragStart({ active }) {
            return `Picked up ${active.data.current?.accessibleLabel}`;
          },
          onDragEnd({ active, over }) {
            if (over) {
              return `Dropped ${active.data.current?.accessibleLabel} over ${over.data.current?.accessibleLabel || over.id}`;
            }
            return `Dropped ${active.data.current?.accessibleLabel}`;
          },
          onDragCancel({ active }) {
            return `Drag cancelled for ${active.data.current?.accessibleLabel}`;
          },
          onDragOver({ active }) {
            return `${active.data.current?.accessibleLabel} currently being dragged`;
          }
        }
      }}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      sensors={sensors}
    >
      <SortableContext
        items={data.map(item => item.key)}
        strategy={verticalListSortingStrategy}
      >
        {/*
          Render the sorted list.
          If the data are <tr> elements, they should be rendered inside a <tbody>.
          Otherwise, for <div> or other elements, this wrapper is flexible.
        */}
        {data.map((item, i) => (
          <SortableItem
            helperClass={helperClass}
            id={item.key}
            itemElement={item}
            key={item.key}
            order={i + 1}
            useDragHandle={useDragHandle}
          />
        ))}
      </SortableContext>

      <SortableOverlay>
        {activeId ? renderItemById(activeId, true) : null}
      </SortableOverlay>
    </DndContext>
  );
};

